interface PasswordStrength {
  score: number; // 0-100
  feedback: string[];
  meets: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumbers: boolean;
    hasSpecialChars: boolean;
    noCommonPatterns: boolean;
    noPersonalInfo: boolean;
  };
  isValid: boolean;
}

interface PersonalInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
}

const COMMON_PASSWORDS = [
  'password', '123456', '123456789', 'qwerty', 'abc123', 'password123',
  'admin', 'letmein', 'welcome', 'monkey', '1234567890', 'Password1',
  'password1', 'qwerty123', 'welcome123', 'admin123', 'Login123',
  'Password123', 'passw0rd', 'p@ssw0rd', 'P@ssw0rd', 'P@ssword123'
];

const SEQUENTIAL_PATTERNS = [
  '123456', '654321', 'abcdef', 'fedcba', 'qwerty', 'asdfgh',
  '1234567890', '0987654321', 'abcdefg', 'gfedcba'
];

export const validatePassword = (
  password: string, 
  personalInfo?: PersonalInfo
): PasswordStrength => {
  let score = 0;
  const feedback: string[] = [];
  
  // Check minimum length (12 characters)
  const hasMinLength = password.length >= 12;
  if (hasMinLength) {
    score += 20;
  } else {
    feedback.push(`Password must be at least 12 characters long (currently ${password.length})`);
  }
  
  // Check for uppercase letters
  const hasUppercase = /[A-Z]/.test(password);
  if (hasUppercase) {
    score += 15;
  } else {
    feedback.push('Include at least one uppercase letter (A-Z)');
  }
  
  // Check for lowercase letters
  const hasLowercase = /[a-z]/.test(password);
  if (hasLowercase) {
    score += 15;
  } else {
    feedback.push('Include at least one lowercase letter (a-z)');
  }
  
  // Check for numbers
  const hasNumbers = /[0-9]/.test(password);
  if (hasNumbers) {
    score += 15;
  } else {
    feedback.push('Include at least one number (0-9)');
  }
  
  // Check for special characters
  const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(password);
  if (hasSpecialChars) {
    score += 15;
  } else {
    feedback.push('Include at least one special character (!@#$%^&*)');
  }
  
  // Check against common passwords
  const lowerPassword = password.toLowerCase();
  const noCommonPasswords = !COMMON_PASSWORDS.some(common => 
    lowerPassword.includes(common.toLowerCase())
  );
  if (noCommonPasswords) {
    score += 10;
  } else {
    feedback.push('Avoid common passwords and dictionary words');
  }
  
  // Check for sequential patterns
  const noSequentialPatterns = !SEQUENTIAL_PATTERNS.some(pattern =>
    lowerPassword.includes(pattern.toLowerCase())
  );
  if (noSequentialPatterns) {
    score += 5;
  } else {
    feedback.push('Avoid sequential patterns like "123456" or "abcdef"');
  }
  
  // Check for repeating characters
  const noRepeatingChars = !/(.)\1{3,}/.test(password);
  if (noRepeatingChars) {
    score += 5;
  } else {
    feedback.push('Avoid repeating the same character 4+ times');
  }
  
  // Check against personal information
  let noPersonalInfo = true;
  if (personalInfo) {
    const personalData = [
      personalInfo.firstName?.toLowerCase(),
      personalInfo.lastName?.toLowerCase(),
      personalInfo.email?.split('@')[0]?.toLowerCase()
    ].filter(Boolean);
    
    for (const data of personalData) {
      if (data && data.length > 2 && lowerPassword.includes(data)) {
        noPersonalInfo = false;
        feedback.push('Do not include your name or email in the password');
        break;
      }
    }
  }
  
  if (noPersonalInfo) {
    score += 10;
  }
  
  // Bonus points for length beyond minimum
  if (password.length > 16) {
    score += Math.min(10, (password.length - 16) * 2);
  }
  
  // Cap the score at 100
  score = Math.min(100, score);
  
  const isValid = hasMinLength && hasUppercase && hasLowercase && 
                  hasNumbers && hasSpecialChars && noCommonPasswords && noPersonalInfo;
  
  return {
    score,
    feedback,
    meets: {
      minLength: hasMinLength,
      hasUppercase,
      hasLowercase,
      hasNumbers,
      hasSpecialChars,
      noCommonPatterns: noCommonPasswords && noSequentialPatterns && noRepeatingChars,
      noPersonalInfo
    },
    isValid
  };
};

export const getPasswordStrengthLabel = (score: number): string => {
  if (score < 30) return 'Very Weak';
  if (score < 50) return 'Weak';
  if (score < 70) return 'Fair';
  if (score < 85) return 'Good';
  return 'Excellent';
};

export const getPasswordStrengthColor = (score: number): string => {
  if (score < 30) return 'hsl(var(--destructive))';
  if (score < 50) return 'hsl(var(--warning))';
  if (score < 70) return 'hsl(38 92% 50%)'; // Orange
  if (score < 85) return 'hsl(142 76% 36%)'; // Green
  return 'hsl(var(--success))';
};